import { ReplayerProxy } from '../ReplayerProxy'
import { VideoPlaceholdersTracker } from './VideoPlaceholdersTracker'
import { TrackedElementDetails } from './TrackedElementDetails'
import { LoggerName, namedLogger } from '../../../../../../utils/logger'

const logger = namedLogger(LoggerName.PlaceholderWorkQueue)

export enum DeferredWorkOperation {
  ShowVideo = 'show-video',
  HideVideo = 'hide-video',
  VideoStateChange = 'video-state-change'
}

export type DeferredWorkForVideoPlaceholders = {
  operation: DeferredWorkOperation
  callback: DeferredWorkForVideoPlaceholdersCB
}
export type DeferredWorkForVideoPlaceholdersCB = (placeholderEl?: HTMLElement) => void

/**
 * At various points during the replay, we will need to operate on video placeholders that don't yet exist in the DOM. It is
 * very common that we get a video start event a couple millis before we observe the associated video placeholder being added
 * to the DOM. The PlaceholderWorkQueue allow us to continue as if the placeholder were present. It holds the work that needs
 * to be performed on those placeholder and executes it once it is added. This code listens for the placeholders as they are
 * added to the dom and executes the queued work when they appear.
 */
export class PlaceholderWorkQueue {
  private workQueue: Map<string, Array<DeferredWorkForVideoPlaceholders>> = new Map()
  // when video placeholders are encountered during onBuild, we track them in this map so that we can place videos into them
  public videoPlaceholdersTracker = new VideoPlaceholdersTracker()

  clearTrackedVideoPlaceholders() {
    logger.debug('clearTrackedVideoPlaceholders called')
    this.videoPlaceholdersTracker.clear()
  }

  /**
   * This method is used to perform work on a video placeholder. If it is available in the dom not, the work is performed
   * immediately, otherwise it is queued.
   * @param videoPlaceholderName
   * @param replayerProxy
   * @param deferredWork
   */
  public doWorkForVideoPlaceholderWhenReady(
    videoPlaceholderName: string | null,
    replayerProxy: ReplayerProxy,
    deferredWork: DeferredWorkForVideoPlaceholders
  ) {
    // Check if the video placeholder has already been built (observed in onBuild and tracked).
    // Play the video if it has, otherwise wait until it is built.

    const noPlaceholderNeeded = !videoPlaceholderName
    if (noPlaceholderNeeded) {
      logger.debug(`doWorkForVideoPlaceholderWhenReady executing immediately (${deferredWork.operation}) because no placeholder is needed`)
      const immediateOperation = deferredWork.callback
      immediateOperation()
    } else {
      const connectedVideoPlaceholder = this.videoPlaceholdersTracker.findPlaceholder(videoPlaceholderName, replayerProxy.getPlayerDocument())
      if (connectedVideoPlaceholder) {
        logger.debug(`doWorkForVideoPlaceholderWhenReady executing immediately (${deferredWork.operation}) for`, videoPlaceholderName)
        // if the node is already built, we can insert the video and play it now
        deferredWork.callback(connectedVideoPlaceholder)
      } else if (deferredWork.operation === DeferredWorkOperation.HideVideo) {
        // if we are processing a hide and the queue contains a show for this placeholder, then just remove the show operation and do nothing.
        const deferredWorkForPlaceholder = this.workQueue.get(videoPlaceholderName)
        if (deferredWorkForPlaceholder) {
          const showOperation = deferredWorkForPlaceholder.find(work => work.operation === DeferredWorkOperation.ShowVideo)

          if (showOperation) {
            logger.debug('doWorkForVideoPlaceholderWhenReady - removing show operation for', videoPlaceholderName)
            this.workQueue.set(
              videoPlaceholderName,
              deferredWorkForPlaceholder.filter(work => work.operation !== DeferredWorkOperation.ShowVideo)
            )
          }
        }
        logger.debug('doWorkForVideoPlaceholderWhenReady - performing hide operation while', videoPlaceholderName, 'is missing from the mirror')
        const hideOperation = deferredWork.callback
        hideOperation()
      } else {
        // otherwise, we need to wait until the node is in the DOM
        this.deferWorkForVideoPlaceholder(videoPlaceholderName, deferredWork)
      }
    }
  }

  private deferWorkForVideoPlaceholder(videoPlaceholderName: string, deferredWork: DeferredWorkForVideoPlaceholders) {
    if (!this.workQueue.has(videoPlaceholderName)) {
      this.workQueue.set(videoPlaceholderName, [])
    }

    this.workQueue.get(videoPlaceholderName)?.push(deferredWork)
    logger.debug('deferWorkForVideoPlaceholder', videoPlaceholderName, {
      console: { workQueue: this.workQueue.get(videoPlaceholderName) }
    })
  }

  /**
   * When we receive an event to play a video, sometimes the video placeholder is not yet present. We solve this by keeping track of the work that
   * needs to be performed, and we execute it once we see the placeholder arrive. The is the code that tracks the video placeholders as they are
   * built in the DOM.
   * @param el
   * @private
   */
  checkForVideoPlaceholderAndExecuteAnyDeferredWork(el: HTMLElement) {
    const videoPlaceholderName = el?.dataset?.[TrackedElementDetails.VIDEO_PLACEHOLDER.attributeCamelName]
    // if the element being added to the dom is one of the video placeholders that deferred work, do it now.
    if (videoPlaceholderName) {
      // the only way for us to get a reference to the placeholder element when processing the video start event is to track it here.
      this.videoPlaceholdersTracker.track(videoPlaceholderName, el)

      // execute the deferred video start event work
      this.checkForDeferredVideoPlaceholderWork(videoPlaceholderName, el)
    }
  }

  /**
   * After a video placeholder has been built, this method checks if there is any deferred work that needs to be performed
   * on it and executes it.
   * @param videoPlaceholderName
   * @param placeholderEl
   */
  checkForDeferredVideoPlaceholderWork(videoPlaceholderName: string, placeholderEl: HTMLElement) {
    const deferredWork = this.workQueue.get(videoPlaceholderName)

    if (deferredWork && deferredWork.length > 0) {
      while (deferredWork && deferredWork.length > 0) {
        const work = deferredWork.shift()!
        logger.debug('found deferred work for', videoPlaceholderName, {
          console: {
            work
          }
        })
        // using requestAnimationFrame will defer this until the container is actually in the DOM.
        window.requestAnimationFrame(() => {
          if (placeholderEl) {
            logger.debug(`executing deferred work ${work.operation} for`, videoPlaceholderName)
            work.callback(placeholderEl)
          } else {
            logger.error(
              `Unable to perform placeholder work for placeholder ${videoPlaceholderName} because it is either not tracked, or connected, or in the mirror`
            )
          }
        })
      }
    }
  }
}
